export enum RenderGenerationType {
  SiloStudio = 'silo_studio',
  BatchRender = 'batch_render',
  LsHotspotStudio = 'ls_hotspot_studio',
  CompositeRender = 'composite_render'
}

export enum RenderGenerationStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
  Cancelled = 'cancelled'
}

export type RenderGenerationResultStatus =
  | RenderGenerationStatus.Pending
  | RenderGenerationStatus.Completed
  | RenderGenerationStatus.Failed;

export interface RenderGenerationCameraOrbit {
  theta_deg: number;
  phi_deg: number;
  radius: number;
}

export interface RenderGenerationVector3 {
  x: number;
  y: number;
  z: number;
}

export interface RenderGenerationQuaternion {
  x: number;
  y: number;
  z: number;
  w: number;
}

export interface RenderGenerationIncomingPayload {
  schema_version: number;
  // All vectors/matrices below live in the SAME world-space (the model-viewer
  // three.js scene). The receiver does not need to know any model-viewer
  // internal convention: place the camera at camera.world_position looking at
  // camera.look_at with camera.up, and place the model by model.matrix_world
  // (or world_position + world_quaternion + scale). This reproduces the exact
  // framing in any engine even if the model is NOT at the origin.
  coordinate_system: {
    up: 'y';
    handedness: 'right';
    units: 'meters';
    angles: 'degrees';
    // Row-major 4x4 world matrices (three.js Matrix4 is column-major in memory;
    // we emit it as row-major nested arrays for engine-agnostic consumption).
    matrix_layout: 'row_major';
  };
  camera: {
    // ---- Engine-agnostic, ready to use (derived from the live scene camera) ----
    // Real camera world position.
    world_position: RenderGenerationVector3;
    // Point the camera looks at (orbit/pivot target) in world space.
    look_at: RenderGenerationVector3;
    // Up vector.
    up: RenderGenerationVector3;
    // Normalized forward direction (look_at - world_position), for convenience.
    forward: RenderGenerationVector3;
    // World orientation quaternion.
    world_quaternion: RenderGenerationQuaternion;
    // Vertical field of view in degrees.
    fov_vertical_deg?: number;
    aspect?: number;
    near?: number;
    far?: number;
    // ---- Human-readable source of truth (model-viewer orbit conventions) ----
    // Orbit relative to look_at, like OrbitControls. theta/phi already in degrees.
    orbit: RenderGenerationCameraOrbit;
  };
  model: {
    // Real model world transform — do NOT assume the model sits at the origin.
    world_position: RenderGenerationVector3;
    world_quaternion: RenderGenerationQuaternion;
    world_scale: RenderGenerationVector3;
    // Full world matrix (row-major 4x4), authoritative for non-trivial placement.
    matrix_world: number[][];
    // Axis-aligned bounding box in world space (meters).
    bbox_world_min?: RenderGenerationVector3;
    bbox_world_max?: RenderGenerationVector3;
    // Local bounding box size and center (meters), independent of placement.
    bbox_size?: RenderGenerationVector3;
    bbox_center?: RenderGenerationVector3;
    // Rendered without a floor/ground shadow.
    has_ground_shadow: false;
  };
  viewport: {
    width?: number;
    height?: number;
    aspect_ratio?: number;
  };
}

export interface CreateRenderGenerationInput {
  generation_type: RenderGenerationType;
  model_id: string;
  user_id?: string | null;
  // Legacy pixel size kept for backward compatibility; the backend resolves
  // the output resolution from aspect_ratio + image_size (fields not
  // supported by the backend yet — sent ahead of the backend rollout).
  requested_width?: number | null;
  requested_height?: number | null;
  aspect_ratio?: string | null;
  image_size?: string | null;
  incoming_payload?: RenderGenerationIncomingPayload | null;
}

// ── Composite render (Composer scene → multi-model render request) ─────────

export interface CompositeRenderVector3 {
  x: number;
  y: number;
  z: number;
}

export interface CompositeRenderQuaternion {
  type: 'quaternion';
  x: number;
  y: number;
  z: number;
  w: number;
}

export interface CompositeRenderModel {
  id: string;
  transform: {
    position: CompositeRenderVector3;
    rotation: CompositeRenderQuaternion;
    scale: CompositeRenderVector3;
  };
  bbox: {
    source: 'client_estimated';
    world: {
      min: CompositeRenderVector3;
      max: CompositeRenderVector3;
      size: CompositeRenderVector3;
      center: CompositeRenderVector3;
    };
  };
}

export interface CompositeRenderCamera {
  id: string;
  name: string;
  type: 'perspective';
  transform: {
    position: CompositeRenderVector3;
    look_at: {
      target: CompositeRenderVector3;
      up: CompositeRenderVector3;
    };
  };
  projection: {
    fov_axis: 'vertical';
    fov_vertical_deg: number;
    aspect: number;
    near: number;
    far: number;
    zoom: number;
    film_gauge_mm: number;
    film_offset_mm: number;
  };
  dof: {
    enabled: boolean;
    focus_distance: number | null;
    f_stop: number | null;
    aperture_blades: number | null;
    aperture_rotation_deg: number | null;
    aperture_ratio: number | null;
  };
}

export interface CompositeRenderIncomingPayload {
  scene: {
    models: CompositeRenderModel[];
    cameras: CompositeRenderCamera[];
  };
}

export interface CreateCompositeRenderGenerationInput {
  generation_type: RenderGenerationType.CompositeRender;
  user_id?: string | null;
  requested_width?: number | null;
  requested_height?: number | null;
  background_hex?: string;
  incoming_payload: CompositeRenderIncomingPayload;
}

export interface CreateRenderGenerationResponse {
  id: string;
  generation_type: RenderGenerationType;
  model_id: string;
}

export interface RenderGenerationResult {
  id: string;
  view_key: string;
  status: RenderGenerationResultStatus;
  image_result: string;
  width: number | null;
  height: number | null;
  metadata: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
}

export interface FetchRenderGenerationResponse {
  id: string;
  generation_type: RenderGenerationType;
  model_id: string;
  status: RenderGenerationStatus;
  error_message: string | null;
  results: RenderGenerationResult[];
  created_at: string;
  updated_at: string;
}
